import { css, cx } from '@lastbrand/web-ui-styled-system/css'

import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { Image } from '@src/quince-components/src'

import type { FeatureGridCellPosition, FeatureGridDSProps } from './feature-grid-ds.types'

const iconWrapClass = css({ width: '40px', height: '40px' })
const iconFillClass = css({ width: 'full', height: 'full', objectFit: 'contain' })

const textWrapClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: { base: 'space-8', lg: 'space-16' },
  width: 'full',
  paddingInline: { base: 'space-8', lg: 0 },
})
const titleClass = css({ textStyle: 'title-12', textTransform: 'uppercase', color: 'black', width: 'full' })
const descClass = css({ textStyle: { base: 'body-14', lg: 'body-16' }, color: 'black', width: 'full' })

// One quadrant of the grid: centered on mobile, left-aligned on desktop, with a 1px divider on the
// sides that face a neighbour (together the four cells draw the cross) and asymmetric inner padding
// so content sits away from that divider.
const cellClass = ({ isTopRow, isLeftColumn, hasRightBorder, hasBottomBorder }: FeatureGridCellPosition) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'space-16',
    justifyContent: 'flex-start',
    alignItems: { base: 'center', lg: 'flex-start' },
    textAlign: { base: 'center', lg: 'left' },
    borderColor: 'black-trans-20',
    borderRightWidth: hasRightBorder ? '1px' : 0,
    borderBottomWidth: hasBottomBorder ? '1px' : 0,
    paddingBlockStart: { base: isTopRow ? 0 : 'space-32', lg: isTopRow ? 'space-40' : 'space-56' },
    paddingBlockEnd: { base: isTopRow ? 'space-32' : 0, lg: isTopRow ? 'space-56' : 'space-40' },
    paddingInlineEnd: { lg: isLeftColumn ? 'space-56' : 0 },
    paddingInlineStart: { lg: isLeftColumn ? 0 : 'space-56' },
  })

interface FeatureGridCellProps {
  content: FeatureGridDSProps['contents'][number]
  position: FeatureGridCellPosition
}

export function FeatureGridCell({ content, position }: FeatureGridCellProps) {
  const { iconProps, heading, description } = content
  const iconAlt = iconProps?.alt || iconProps?.title || ''
  return (
    <div className={cellClass(position)}>
      {iconProps?.url ? (
        <div className={iconWrapClass}>
          <Image {...iconProps} alt={iconAlt} className={cx(iconFillClass, iconProps.className)} />
        </div>
      ) : null}
      <div className={textWrapClass}>
        {heading ? <span className={titleClass}>{heading}</span> : null}
        {description?.content ? <RichTextDS content={description.content} className={descClass} /> : null}
      </div>
    </div>
  )
}
